import { ActivityFieldKey, type ActivityFieldLayoutItem } from '@api';

import {
  ACTIVITY_FIELD_CARD_DISPLAY_MENU_I18N_PREFIX,
  ACTIVITY_FIELD_CARD_MENU_LABEL_KEY,
  ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER,
  SHIPPED_ACTIVITY_FIELD_KEYS,
} from './activity-edit-field-layout.constants';

/** Metadata fields rendered inside the colored card header grid (not `name` or visibility). */
export const ACTIVITY_EDIT_HEADER_GRID_FIELD_KEYS: readonly ActivityFieldKey[] = [
  ActivityFieldKey.TYPE,
  ActivityFieldKey.WORKFORM,
  ActivityFieldKey.DURATION,
  ActivityFieldKey.GRADE,
  ActivityFieldKey.SUBJECT,
  ActivityFieldKey.LOCATIONS,
];

/** Catalogue fields rendered in the white card body. */
export const ACTIVITY_EDIT_BODY_FIELD_KEYS: readonly ActivityFieldKey[] = [
  ActivityFieldKey.TOOLS,
  ActivityFieldKey.DESCRIPTION,
  ActivityFieldKey.INSTRUCTIONS,
  ActivityFieldKey.SECTIONS,
  ActivityFieldKey.CONTEXT_DOCUMENTS,
];

export type CardDisplayMenuFieldInputKind = 'choice' | 'text';

/**
 * Transloco key for a gear-menu row label.
 *
 * @param fieldKey - Catalogue field the row represents.
 * @returns Transloco key for that field's gear-menu label.
 */
export function cardMenuLabelKey(fieldKey: ActivityFieldKey): string {
  return ACTIVITY_FIELD_CARD_MENU_LABEL_KEY[fieldKey];
}

const ACTIVITY_FIELD_CARD_MENU_INPUT_KIND: Record<ActivityFieldKey, CardDisplayMenuFieldInputKind> = {
  [ActivityFieldKey.NAME]: 'text',
  [ActivityFieldKey.TYPE]: 'choice',
  [ActivityFieldKey.WORKFORM]: 'choice',
  [ActivityFieldKey.DURATION]: 'text',
  [ActivityFieldKey.GRADE]: 'choice',
  [ActivityFieldKey.SUBJECT]: 'choice',
  [ActivityFieldKey.LOCATIONS]: 'choice',
  [ActivityFieldKey.TOOLS]: 'choice',
  [ActivityFieldKey.DESCRIPTION]: 'text',
  [ActivityFieldKey.INSTRUCTIONS]: 'text',
  [ActivityFieldKey.SECTIONS]: 'text',
  [ActivityFieldKey.CONTEXT_DOCUMENTS]: 'choice',
};

/**
 * Material Symbols name for the gear-menu type hint on an activity card field row.
 *
 * @param fieldKey - Catalogue field the row represents.
 * @returns Icon ligature name (outlined set).
 */
export function activityFieldCardMenuIcon(fieldKey: ActivityFieldKey): string {
  return cardMenuInputKind(fieldKey) === 'choice' ? 'dropdown_menu' : 'short_text';
}

/**
 * Gear-menu hint: picker (choice) vs free entry (free text / rich text).
 *
 * @param fieldKey - Catalogue field the row represents.
 * @returns `choice` for picker fields, `text` for free-form fields.
 */
export function cardMenuInputKind(fieldKey: ActivityFieldKey): CardDisplayMenuFieldInputKind {
  return ACTIVITY_FIELD_CARD_MENU_INPUT_KIND[fieldKey];
}

/**
 * Transloco key for the gear-menu input-kind hint (picker / free entry).
 *
 * @param kind - {@link cardMenuInputKind} result.
 * @returns i18n key under the card display menu prefix.
 */
export function cardMenuInputKindLabelKey(kind: CardDisplayMenuFieldInputKind): string {
  return kind === 'choice'
    ? `${ACTIVITY_FIELD_CARD_DISPLAY_MENU_I18N_PREFIX}.FIELD_INPUT_KIND.CHOICE`
    : `${ACTIVITY_FIELD_CARD_DISPLAY_MENU_I18N_PREFIX}.FIELD_INPUT_KIND.TEXT`;
}

/**
 * Inserts catalogue fields missing from a stored layout, visible, at their catalogue slot, then
 * recompacts `sortOrder`. The gear menu sorts by that `sortOrder`.
 *
 * @param layout - A non-empty layout, typically {@link effectiveFieldLayout}.
 * @returns Layout with every catalogue key, compact `sortOrder` from 1.
 */
export function completeCatalogueLayout(layout: readonly ActivityFieldLayoutItem[]): ActivityFieldLayoutItem[] {
  // Project target is ES2022; Array#toSorted needs ES2023 lib typings.
  // eslint-disable-next-line unicorn/no-array-sort -- copy before sort
  const ordered = [...layout].sort((a, b) => a.sortOrder - b.sortOrder);
  const present = new Set(ordered.map((item) => item.fieldKey));
  const completed = ordered.map((item) => ({ ...item }));

  for (const fieldKey of ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER) {
    if (present.has(fieldKey)) {
      continue;
    }

    completed.splice(catalogueInsertIndex(completed, fieldKey), 0, {
      fieldKey,
      sortOrder: 0,
      visible: true,
    });
    present.add(fieldKey);
  }

  return completed.map((item, index) => ({
    ...item,
    sortOrder: index + 1,
  }));
}

/**
 * Default rows (catalogue order, all visible) when GET detail has no layout yet.
 *
 * @returns One visible row per catalogue key, with `sortOrder` starting at 1.
 */
export function defaultFieldLayoutItems(): ActivityFieldLayoutItem[] {
  return ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER.map((fieldKey, index) => ({
    fieldKey,
    sortOrder: index + 1,
    visible: true,
  }));
}

/**
 * Server layout or {@link defaultFieldLayoutItems} when the draft has no rows yet.
 *
 * @param layout - Stored field layout, possibly empty.
 * @returns A copy of `layout`, or the default catalogue when it is empty.
 */
export function effectiveFieldLayout(layout: readonly ActivityFieldLayoutItem[]): ActivityFieldLayoutItem[] {
  if (layout.length === 0) {
    return defaultFieldLayoutItems();
  }

  return layout.map((item) => ({ ...item }));
}

/**
 * Whether a catalogue field is shown on the edit card. `name` is always shown (KT-903).
 * When the layout row is missing (e.g. empty draft), defaults to visible so legacy/tests stay stable.
 *
 * @param layout - Stored field layout.
 * @param fieldKey - Catalogue field to check.
 * @returns True when the field is shown.
 */
export function isFieldVisibleInLayout(
  layout: readonly ActivityFieldLayoutItem[] | undefined,
  fieldKey: ActivityFieldKey,
): boolean {
  if (fieldKey === ActivityFieldKey.NAME) {
    return true;
  }

  if (!layout) {
    return true;
  }

  const item = layout.find((entry) => entry.fieldKey === fieldKey);
  if (!item) {
    return true;
  }

  return item.visible;
}

/**
 * Server `fieldLayout` restricted to {@link SHIPPED_ACTIVITY_FIELD_KEYS}, sorted by `sortOrder`.
 *
 * @param layout - Stored field layout, in any order.
 * @returns Shipped rows in panel order.
 */
export function layoutItemsForFieldPanel(layout: readonly ActivityFieldLayoutItem[]): ActivityFieldLayoutItem[] {
  const shipped = new Set<ActivityFieldKey>(SHIPPED_ACTIVITY_FIELD_KEYS);

  const items = effectiveFieldLayout(layout).filter((item) => shipped.has(item.fieldKey));
  // Project target is ES2022; Array#toSorted needs ES2023 lib typings.
  // eslint-disable-next-line unicorn/no-array-sort -- filter() already allocated a new array
  return items.sort((a, b) => a.sortOrder - b.sortOrder);
}

/**
 * Field keys for one edit-card region in layout `sortOrder`, falling back to catalogue order when the
 * draft has no layout rows yet (legacy tests / pre-init instances).
 *
 * @param layout - Stored field layout, possibly empty.
 * @param regionKeys - Catalogue keys that belong to the region.
 * @returns Region keys in display order.
 */
export function orderedFieldKeysInEditRegion(
  layout: readonly ActivityFieldLayoutItem[],
  regionKeys: readonly ActivityFieldKey[],
): ActivityFieldKey[] {
  const regionSet = new Set<ActivityFieldKey>(regionKeys);

  if (layout.length === 0) {
    return ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER.filter((key) => regionSet.has(key));
  }

  const fromLayout = layoutItemsForFieldPanel(layout)
    .filter((item) => regionSet.has(item.fieldKey))
    .map((item) => item.fieldKey);
  const present = new Set(fromLayout);
  const missing = ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER.filter((key) => regionSet.has(key) && !present.has(key));

  return [...fromLayout, ...missing];
}

/**
 * Copies `layout` with one field's visibility changed. `name` stays visible.
 *
 * @param layout - Stored field layout.
 * @param fieldKey - Catalogue field to update.
 * @param visible - Next visibility. Ignored for `name`.
 * @returns A new layout with the updated row.
 */
export function withFieldLayoutVisibility(
  layout: readonly ActivityFieldLayoutItem[],
  fieldKey: ActivityFieldKey,
  visible: boolean,
): ActivityFieldLayoutItem[] {
  return layout.map((item) => {
    if (item.fieldKey !== fieldKey) {
      return { ...item };
    }

    return {
      ...item,
      visible: fieldKey === ActivityFieldKey.NAME ? true : visible,
    };
  });
}

/**
 * Index at which a missing catalogue field is inserted: after the nearest earlier catalogue
 * neighbour already in `items`, otherwise before the nearest later one.
 *
 * @param items - Rows already placed, in display order.
 * @param fieldKey - Catalogue key that is not yet in `items`.
 * @returns Insertion index in `items`.
 */
function catalogueInsertIndex(items: readonly ActivityFieldLayoutItem[], fieldKey: ActivityFieldKey): number {
  const catalogueIndex = ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER.indexOf(fieldKey);

  for (let index = catalogueIndex - 1; index >= 0; index -= 1) {
    const predecessor = ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER[index];
    const at = items.findIndex((item) => item.fieldKey === predecessor);
    if (at !== -1) {
      return at + 1;
    }
  }

  for (let index = catalogueIndex + 1; index < ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER.length; index += 1) {
    const successor = ACTIVITY_FIELD_LAYOUT_CATALOGUE_ORDER[index];
    const at = items.findIndex((item) => item.fieldKey === successor);
    if (at !== -1) {
      return at;
    }
  }

  return items.length;
}
